import { computed, ref } from 'vue'

import { runIsActive } from '@/helpers/parsley'

export const POLL_INTERVAL_MS = 2500
export const MAX_POLL_FAILURES = 5

// Module scope, not component state: a run started on the Settings page has to
// still be visible, and still be watched, on the mapping page.
const run = ref(null)
const watching = ref(false)
const lost = ref(false)

let timer = null
let generation = 0

export function clearParsleyRun() {
  generation += 1
  if (timer) clearTimeout(timer)
  timer = null
  run.value = null
  watching.value = false
  lost.value = false
}

export function trackParsleyRun({ run: started, fetchRun }) {
  clearParsleyRun()
  const mine = (generation += 1)
  run.value = started
  if (!runIsActive(started)) return

  watching.value = true
  let failures = 0

  const schedule = () => {
    timer = setTimeout(poll, POLL_INTERVAL_MS)
  }

  const poll = async () => {
    if (mine !== generation) return
    try {
      const latest = await fetchRun(started.id)
      if (mine !== generation) return
      failures = 0
      run.value = latest
      if (runIsActive(latest)) schedule()
      else watching.value = false
    } catch (error) {
      if (mine !== generation) return
      failures += 1
      if (failures < MAX_POLL_FAILURES) {
        schedule()
        return
      }
      // Losing the connection says nothing about the run, so this stops
      // watching rather than claiming the run failed.
      watching.value = false
      lost.value = true
    }
  }

  schedule()
}

// A run started elsewhere is already being tracked, so it is left alone. A run
// list that comes back after something newer was tracked or cleared is dropped.
export async function showLatestParsleyRun({ fetchRuns, fetchRun }) {
  if (run.value) return
  const mine = generation
  const [latest] = await fetchRuns()
  if (mine !== generation || !latest) return
  trackParsleyRun({ run: latest, fetchRun })
}

export function useParsleyRunTracker() {
  return {
    run,
    running: computed(() => watching.value),
    finished: computed(() => run.value !== null && !runIsActive(run.value)),
    lost: computed(() => lost.value),
    clear: clearParsleyRun
  }
}
